'use client';

import cx from 'classnames';
import { ReactNode } from 'react';

import { ArrowCursorPagination, ArrowPagination, PaginationProps } from '../Pagination';
import { Spinner } from '../Spinner';
import DocumentSearchIcon from '../assets/svgs/document-search.svg';
import styles from './table.module.scss';

interface Props<I> {
  tHeader?: ReactNode;
  isLoading?: boolean;
  items: I[] | undefined;
  renderRow: (item: I, index: number) => ReactNode;
  noData?: ReactNode;
  withHover?: boolean;
  pagination?: Omit<PaginationProps, 'isLoading'>;
  cursorPagination?: {
    hasPrev: boolean;
    hasNext: boolean;
    cursorKey: keyof I;
    onChange: (params: { before?: string; after?: string }) => void;
  };
  className?: string;
}

export const Table = <I,>({
  tHeader,
  isLoading,
  items,
  renderRow,
  noData,
  withHover,
  pagination,
  cursorPagination,
  className,
}: Props<I>) => {
  return (
    <div className={cx(styles.wrapper, className, { [styles.withHover]: withHover })}>
      <table>
        {tHeader}
        <tbody>{!isLoading && items?.map((item, i) => renderRow(item, i))}</tbody>
      </table>
      {isLoading && (
        <div className={styles.loading}>
          <Spinner center />
        </div>
      )}
      {!isLoading && (!items || items?.length === 0) && (
        <div className={styles.noData}>
          <DocumentSearchIcon />
          <div>{noData ?? 'No data found'}</div>
        </div>
      )}
      {items && pagination && (
        <div className={styles.pagination}>
          <ArrowPagination isLoading={isLoading} {...pagination} />
        </div>
      )}
      {items && cursorPagination && (
        <div className={styles.pagination}>
          <ArrowCursorPagination
            isLoading={isLoading}
            prevKey={items && cursorPagination.hasPrev ? (items[0]?.[cursorPagination.cursorKey] as string) : undefined}
            nextKey={
              items && cursorPagination.hasNext
                ? (items[items.length - 1]?.[cursorPagination.cursorKey] as string)
                : undefined
            }
            {...cursorPagination}
          />
        </div>
      )}
    </div>
  );
};
